import { useMemo, useState } from "react";
import { AtSign, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  useAronMailAliases,
  useAronMailAliasTargets,
  useCreateAronMailAlias,
  useDeleteAronMailAlias,
  useSetAronMailAliasActive,
} from "../api/aronMailApi";

/** Extract a human message from a DRF { data, errors, meta } error payload. */
function apiError(err: unknown): string | null {
  const e = err as { response?: { data?: { errors?: unknown } } };
  const errors = e?.response?.data?.errors;
  if (!errors) return null;
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return String(errors[0]);
  if (typeof errors === "object") {
    const first = Object.values(errors as Record<string, unknown>)[0];
    return Array.isArray(first) ? String(first[0]) : String(first);
  }
  return null;
}

export function AronMailAliasesPage() {
  usePageTitle("Aliasy poczty ARON");

  const { data: aliases = [], isLoading } = useAronMailAliases();
  const { data: targets = [] } = useAronMailAliasTargets();
  const createAlias = useCreateAronMailAlias();
  const setActive = useSetAronMailAliasActive();
  const del = useDeleteAronMailAlias();

  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const targetName = useMemo(
    () => new Map(targets.map((t) => [t.id, t.name])),
    [targets],
  );

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!userId || !email.trim()) {
      setError("Wybierz pracownika i podaj adres e-mail.");
      return;
    }
    createAlias.mutate(
      { user: userId, email: email.trim() },
      {
        onSuccess: () => setEmail(""),
        onError: (err) => setError(apiError(err) ?? "Nie udało się dodać aliasu."),
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-gray-100">
          <AtSign size={18} /> Aliasy poczty ARON
        </h1>
        <p className="mt-1 max-w-3xl text-xs text-gray-500 dark:text-gray-400">
          Dodatkowe / prywatne adresy e-mail zmapowane na pracownika. Wiadomość
          przychodząca na skrzynkę ARON z <b>aktywnego</b> aliasu jest traktowana tak,
          jakby wysłał ją ten pracownik (np. może przysłać fakturę kosztową do
          wprowadzenia). Aliasy nadaje wyłącznie CEO. Alias działa tylko dopóki jego
          właściciel pozostaje aktywnym pracownikiem z dostępem do ARON.
        </p>
      </section>

      {/* Dodawanie aliasu */}
      <section className="border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-semibold text-gray-800 dark:text-gray-200">
          Nowy alias
        </h2>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Pracownik
            </span>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="min-w-[220px] border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            >
              <option value="">— wybierz —</option>
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.email})
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Adres e-mail (prywatny / zewnętrzny)
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="np. jan.kowalski@gmail.com"
              className="min-w-[260px] border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
          <Button type="submit" size="sm" disabled={createAlias.isPending}>
            <Plus size={14} /> Dodaj alias
          </Button>
        </form>
        {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      </section>

      {/* Lista aliasów */}
      <section className="overflow-x-auto border border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest">
                Adres e-mail
              </th>
              <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest">
                Pracownik
              </th>
              <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest">
                Aktywny
              </th>
              <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest">
                Dodał
              </th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Ładowanie…
                </td>
              </tr>
            )}
            {!isLoading && aliases.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Brak aliasów. Dodaj pierwszy powyżej.
                </td>
              </tr>
            )}
            {aliases.map((a) => (
              <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 font-mono text-gray-800 dark:text-gray-200">
                  {a.email}
                </td>
                <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                  {a.user_name || targetName.get(a.user) || a.user}
                </td>
                <td className="px-4 py-2">
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={a.is_active}
                      onChange={() =>
                        setActive.mutate({ id: a.id, is_active: !a.is_active })
                      }
                      className="h-4 w-4 accent-[var(--color-primary)]"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {a.is_active ? "Aktywny" : "Wyłączony"}
                    </span>
                  </label>
                </td>
                <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                  {a.created_by_name || "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Usunąć alias ${a.email}?`)) del.mutate(a.id);
                    }}
                    className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Usuń alias"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
